import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { mergeRegister } from "@lexical/utils";
import Icon from "@mdi/react";
import {
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiCheck,
  mdiClose,
} from "@mdi/js";

const getSelectedLinkNode = (selection) => {
  if (!$isRangeSelection(selection)) return null;
  const node = selection.anchor.getNode();
  const parent = node.getParent();
  if ($isLinkNode(parent)) return parent;
  if ($isLinkNode(node)) return node;
  return null;
};

const FloatingLinkEditor = () => {
  const [editor] = useLexicalComposerContext();
  const inputRef = useRef(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [editedUrl, setEditedUrl] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const nativeSelection = window.getSelection();
    if (!nativeSelection || nativeSelection.rangeCount === 0) return;
    const range = nativeSelection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;
    const editorRect = editorRoot.getBoundingClientRect();
    setPosition({
      top: rect.bottom - editorRect.top + 8,
      left: rect.left - editorRect.left,
    });
  }, [editor]);

  const updateLink = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      const linkNode = getSelectedLinkNode(selection);
      if (linkNode) {
        setLinkUrl(linkNode.getURL());
        setIsVisible(true);
        updatePosition();
      } else {
        setIsVisible(false);
        setIsEditing(false);
        setLinkUrl("");
      }
    });
  }, [editor, updatePosition]);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLink();
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => updateLink());
      })
    );
  }, [editor, updateLink]);

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus();
  }, [isEditing]);

  const handleEdit = () => {
    setEditedUrl(linkUrl);
    setIsEditing(true);
  };

  const handleConfirm = () => {
    if (editedUrl) editor.dispatchCommand(TOGGLE_LINK_COMMAND, editedUrl);
    setIsEditing(false);
  };

  const handleRemove = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setIsVisible(false);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
    }
  };

  if (!isVisible) return null;

  const portalTarget = editor.getRootElement()?.parentElement || document.body;

  return createPortal(
    <div
      className="contract-floating-link"
      style={{ top: position.top, left: position.left }}
    >
      {isEditing ? (
        <div className="contract-floating-link-edit">
          <input
            ref={inputRef}
            type="url"
            className="contract-floating-link-input"
            value={editedUrl}
            onChange={(e) => setEditedUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="https://"
          />
          <button
            type="button"
            className="contract-floating-link-btn confirm"
            onClick={handleConfirm}
          >
            <Icon path={mdiCheck} size={0.67} />
          </button>
          <button
            type="button"
            className="contract-floating-link-btn"
            onClick={() => setIsEditing(false)}
          >
            <Icon path={mdiClose} size={0.67} />
          </button>
        </div>
      ) : (
        <div className="contract-floating-link-view">
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="contract-floating-link-url"
          >
            {linkUrl}
          </a>
          <button
            type="button"
            className="contract-floating-link-btn"
            onClick={handleEdit}
          >
            <Icon path={mdiPencilOutline} size={0.67} />
          </button>
          <button
            type="button"
            className="contract-floating-link-btn remove"
            onClick={handleRemove}
          >
            <Icon path={mdiTrashCanOutline} size={0.67} />
          </button>
        </div>
      )}
    </div>,
    portalTarget
  );
};

export default FloatingLinkEditor;
